import { Component, Input } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DataResponse, GetUserPrivacySettingsResponse, UpdateUserPrivacySettingsRequest, UpdateUserPrivacySettingsResponse, UserPrivacySettings, VisibilityType } from "@apiserver-rest/ponder-data-client-base";
import { AuthSession } from "@ponder-core/auth/auth-session";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { SvgIconComponent } from "@ponder-shared/components/svg-icon.component";
import { SvgIcon } from "@ponder-shared/utils/svgIcons";
import { VisibilityTypes } from "@ponder-shared/utils/visbilityTypes";
import { take } from "rxjs";

@Component({
    selector: "user-privacy-settings-change",
    imports: [BusySpinComponent, ReactiveFormsModule, SvgIconComponent],
    templateUrl: "./user-account-privacy-change.component.html"
})
export class UserAccountPrivacyChangeComponent {
    @Input({ required: true }) public authSession?: AuthSession;
    
    public readonly checkSvgIcon = SvgIcon.Check;
    
    public updatePrivacyForm!: FormGroup;
    public visibilityTypes = VisibilityTypes;

    public isLoading: boolean = false;
    public loadingSuccess: boolean = false;

    private currPrivacy?: UserPrivacySettings;
    private hasPrivacyChanged: boolean = false;
    public isUpdatingPrivacy: boolean = false;
    public updatingPrivacyError?: string;
    public updateSuccess?: boolean;

    public constructor(formBuilder: FormBuilder, private ponderDataClient: PonderDataClient) {
        this.updatePrivacyForm = formBuilder.group({
            profile: [VisibilityType.Public],
            collection: [VisibilityType.Public],
            decks: [VisibilityType.Public],
            stats: [VisibilityType.Public],
            friendRequests: [true]
        });

        this.updatePrivacyForm.valueChanges.subscribe(() => { this.onPrivacyChanged() });
        this.updatePrivacyForm.disable();
    }

    public ngOnChanges(): void {
        this.getUserPrivacySettings();
    }

    private getUserPrivacySettings(): void {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.loadingSuccess = false;

        this.ponderDataClient
            .userSettingsPrivacyGet()
            .pipe(take(1))
            .subscribe({
                next: (response: GetUserPrivacySettingsResponse) => {
                    if (response.success && response.settings) {
                        this.currPrivacy = response.settings;

                        this.updatePrivacyForm.setValue({
                            profile: this.currPrivacy.visibility.profile,
                            collection: this.currPrivacy.visibility.collection,
                            decks: this.currPrivacy.visibility.decks,
                            stats: this.currPrivacy.visibility.stats,
                            friendRequests: this.currPrivacy.userContact.allowFriendRequests
                        });

                        this.updatePrivacyForm.enable();
                        this.loadingSuccess = true;
                    }
                }
            })
            .add(() => this.isLoading = false);
    }

    private onPrivacyChanged(): void {
        if (!this.currPrivacy) {
            return;
        }

        this.updateSuccess = undefined;

        this.hasPrivacyChanged = 
            (this.currPrivacy.visibility.profile != this.updatePrivacyForm.get("profile")!.value) ||
            (this.currPrivacy.visibility.collection != this.updatePrivacyForm.get("collection")!.value) ||
            (this.currPrivacy.visibility.decks != this.updatePrivacyForm.get("decks")!.value) ||
            (this.currPrivacy.visibility.stats != this.updatePrivacyForm.get("stats")!.value) ||
            (this.currPrivacy.userContact.allowFriendRequests != this.updatePrivacyForm.get("friendRequests")!.value);
    }

    public canSubmitPrivacyChanges(): boolean {
        return this.updatePrivacyForm.valid && this.hasPrivacyChanged && !this.isUpdatingPrivacy;
    }

    public onSavePrivacySettingsClicked(): void {
        if (!this.canSubmitPrivacyChanges()) {
            return;
        }

        this.isUpdatingPrivacy = true;
        this.updatingPrivacyError = undefined;
        this.updateSuccess = undefined;
        this.updatePrivacyForm.disable();

        var request = new UpdateUserPrivacySettingsRequest({
            profileVisibility: this.updatePrivacyForm.get("profile")!.value,
            collectionVisibility: this.updatePrivacyForm.get("collection")!.value,
            decksVisibility: this.updatePrivacyForm.get("decks")!.value,
            statsVisibility: this.updatePrivacyForm.get("stats")!.value,
            allowFriendRequests: this.updatePrivacyForm.get("friendRequests")!.value
        });

        this.ponderDataClient
            .userSettingsPrivacyPatch(request)
            .pipe(take(1))
            .subscribe({
                next: (response: UpdateUserPrivacySettingsResponse) => {
                    if (response.success) {
                        if (response.wasUpdated && response.updatedSettings) {
                            this.currPrivacy = response.updatedSettings;
                        }
                        this.hasPrivacyChanged = false;
                        this.updateSuccess = true;
                    } else {
                        this.updatingPrivacyError = "Failed to update privacy settings.";
                    }
                },
                error: () => {
                    this.updatingPrivacyError = "Failed to update privacy settings.";
                }
            })
            .add(() => { 
                this.isUpdatingPrivacy = false;
                this.updatePrivacyForm.enable({ emitEvent: false });
            });
    }
}